import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicNav from '../components/PublicNav';
import Seo from '../components/Seo';
import CorpFooter from '../components/CorpFooter';
import HeroVisual from '../components/HeroVisual';
import Faq from '../components/Faq';
import Icon from '../components/Icon';
import useScrollReveal from '../hooks/useScrollReveal';
import usePublicSettings from '../hooks/usePublicSettings';

function Section({ id, children, className = '' }) {
  const ref = useScrollReveal();
  return (
    <section id={id} ref={ref} className={`section ${className}`}>
      <div className="container">{children}</div>
    </section>
  );
}

export default function Landing() {
  const { t } = useTranslation();
  const settings = usePublicSettings();
  const { pricing } = settings;

  useEffect(() => {
    // #hash ile gelindiyse ilgili bölüme kaydır
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, []);

  const features = [
    ['gauge', 'feature_health_title', 'feature_health_desc'],
    ['activity', 'feature_economy_title', 'feature_economy_desc'],
    ['trending-down', 'feature_churn_title', 'feature_churn_desc'],
    ['bell', 'feature_notify_title', 'feature_notify_desc'],
    ['trending-up', 'feature_ads_title', 'feature_ads_desc'],
  ];

  const detects = [
    ['activity', 'econ', 'detect_econ_t', 'detect_econ_d'],
    ['users', 'players', 'detect_players_t', 'detect_players_d'],
    ['shield', 'cheat', 'detect_cheat_t', 'detect_cheat_d'],
    ['grid', 'content', 'detect_content_t', 'detect_content_d'],
  ];

  const monthlyEq = {
    monthly: pricing.monthly,
    quarterly: Math.round(pricing.quarterly / 3),
    biannual: Math.round(pricing.biannual / 6),
  };
  const plans = [
    { key: 'monthly', total: pricing.monthly, featured: false },
    { key: 'quarterly', total: pricing.quarterly, featured: true },
    { key: 'biannual', total: pricing.biannual, featured: false },
  ];

  const faqItems = [1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => ({ q: t(`faq.q${n}`), a: t(`faq.a${n}`) }));

  return (
    <>
      <Seo />
      <PublicNav />
      <main className="page-enter">
        {/* HERO */}
        <section className="hero">
          <div className="hero-mesh" aria-hidden="true">
            <span className="b1" /><span className="b2" /><span className="b3" />
          </div>
          <div className="container hero-grid">
            <div>
              <span className="eyebrow">{t('landing.eyebrow')}</span>
              <h1>{t('landing.hero_title')}</h1>
              <p className="lede">{t('landing.hero_subtitle')}</p>
              <div className="hero-actions">
                <Link to="/register" className="btn btn-lg">
                  {t('landing.hero_cta')} <Icon name="arrow" size={15} />
                </Link>
                <a href="#how" className="btn btn-ghost btn-lg">{t('landing.hero_secondary')}</a>
              </div>
              <div className="hero-trust">
                <span>{t('landing.trust_readonly')}</span><span className="dot-sep" />
                <span>{t('landing.trust_setup')}</span><span className="dot-sep" />
                <span>{t('landing.trust_agnostic')}</span>
              </div>
            </div>
            <HeroVisual />
          </div>
        </section>

        {/* PROBLEM / ÇÖZÜM */}
        <Section id="neden">
          <div className="section-head reveal">
            <span className="eyebrow">{t('landing.why_eyebrow')}</span>
            <h2>{t('landing.why_title')}</h2>
            <p>{t('landing.why_sub')}</p>
          </div>
          <div className="pain-grid">
            <div className="card pain-card reveal d1">
              <div className="pain-ico"><Icon name="alert-triangle" size={20} /></div>
              <h3>{t('landing.pain1_t')}</h3>
              <p>{t('landing.pain1_d')}</p>
            </div>
            <div className="card pain-card reveal d2">
              <div className="pain-ico"><Icon name="trending-down" size={20} /></div>
              <h3>{t('landing.pain2_t')}</h3>
              <p>{t('landing.pain2_d')}</p>
            </div>
          </div>
          <div className="solution-band reveal d3">
            <h3>{t('landing.solution_t')}</h3>
            <p style={{ maxWidth: '38rem', margin: '0 auto' }}>{t('landing.solution_d')}</p>
          </div>
        </Section>

        <div className="container"><div className="divider" /></div>

        {/* ÖZELLİKLER */}
        <Section id="features">
          <div className="section-head reveal">
            <span className="eyebrow">{t('nav.features')}</span>
            <h2>{t('landing.features_title')}</h2>
            <p>{t('landing.features_sub')}</p>
          </div>
          <div className="feature-grid">
            {features.map(([icon, title, desc], i) => (
              <div className={`card card-hover feature reveal d${(i % 4) + 1}`} key={title}>
                <div className="ico"><Icon name={icon} size={20} /></div>
                <h3>{t(`landing.${title}`)}</h3>
                <p>{t(`landing.${desc}`)}</p>
              </div>
            ))}
          </div>
        </Section>

        <div className="container"><div className="divider" /></div>

        {/* NEYİ YAKALAR */}
        <Section id="detect">
          <div className="section-head reveal">
            <span className="eyebrow">{t('landing.detect_eyebrow')}</span>
            <h2>{t('landing.detect_title')}</h2>
            <p>{t('landing.detect_sub')}</p>
          </div>
          <div className="detect-list">
            {detects.map(([icon, tone, tk, dk], i) => (
              <div className={`card detect-row reveal d${i + 1}`} key={tk}>
                <div className={`d-ico tone-${tone}`}><Icon name={icon} size={20} /></div>
                <div>
                  <h3>{t(`landing.${tk}`)}</h3>
                  <p style={{ marginBottom: 0 }}>{t(`landing.${dk}`)}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <div className="container"><div className="divider" /></div>

        {/* BÜYÜME */}
        <Section id="grow">
          <div className="section-head reveal">
            <span className="eyebrow">{t('landing.grow_eyebrow')}</span>
            <h2>{t('landing.grow_title')}</h2>
            <p>{t('landing.grow_sub')}</p>
          </div>
          <div className="steps">
            {[1, 2, 3, 4].map((n) => (
              <div className={`card feature step reveal d${n}`} key={n}>
                <span className="num">{n}</span>
                <p>{t(`landing.grow_step${n}`)}</p>
              </div>
            ))}
          </div>
          <p className="pricing-note reveal d3">{t('landing.grow_note')}</p>
        </Section>

        <div className="container"><div className="divider" /></div>

        {/* NASIL ÇALIŞIR */}
        <Section id="how">
          <div className="section-head reveal">
            <span className="eyebrow">{t('nav.how')}</span>
            <h2>{t('landing.how_title')}</h2>
            <p>{t('landing.how_sub')}</p>
          </div>
          <div className="steps">
            {[1, 2, 3].map((n) => (
              <div className={`card feature step reveal d${n}`} key={n}>
                <span className="num">{n}</span>
                <p>{t(`landing.how_step${n}`)}</p>
              </div>
            ))}
          </div>
        </Section>

        <div className="container"><div className="divider" /></div>

        {/* FİYATLANDIRMA */}
        <Section id="pricing">
          <div className="section-head reveal">
            <span className="eyebrow">{t('nav.pricing')}</span>
            <h2>{t('landing.pricing_title')}</h2>
            <p>{t('landing.pricing_sub')}</p>
          </div>
          <div className="pricing-grid">
            {plans.map((p, i) => {
              const savePct = Math.round((1 - monthlyEq[p.key] / pricing.monthly) * 100);
              return (
                <div className={`card price-card reveal d${i + 1} ${p.featured ? 'featured' : ''}`} key={p.key}>
                  {p.featured && <span className="tag">{t('landing.pricing_quarterly_hint')}</span>}
                  <div className="plan-name">{t(`checkout.plan_${p.key}`)}</div>
                  <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '2rem', fontWeight: 600, margin: '8px 0 2px' }}>
                    ₺{monthlyEq[p.key]}<span style={{ fontSize: '0.8rem', color: 'var(--text-3)', fontWeight: 400 }}> {t('checkout.per_month')}</span>
                  </div>
                  <div className="plan-hint">
                    {t(`checkout.billed_${p.key}`)} — ₺{p.total}
                  </div>
                  {savePct > 0 && <div className="badge ok" style={{ marginBottom: 14 }}>{t('checkout.save_pct', { pct: savePct })}</div>}
                  <ul style={{ listStyle: 'none', textAlign: 'left', margin: '4px 0 16px', display: 'grid', gap: 8, fontSize: 13 }}>
                    {[1, 2, 3, 4, 5].map((f) => (
                      <li key={f} style={{ display: 'flex', gap: 8, color: 'var(--text-2)' }}>
                        <Icon name="check" size={14} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
                        {t(`landing.plan_feature${f}`)}
                      </li>
                    ))}
                  </ul>
                  <Link to="/register" className={`btn btn-block ${p.featured ? '' : 'btn-subtle'}`}>{t('nav.register')}</Link>
                </div>
              );
            })}
          </div>
          <p className="pricing-note">
            {t('landing.pricing_multi', { pct: settings.plans?.extraServerDiscountPct ?? 35 })}
            <br />
            {t('landing.pricing_note')}
          </p>
        </Section>

        <div className="container"><div className="divider" /></div>

        {/* SSS */}
        <Section id="faq">
          <div className="section-head reveal">
            <span className="eyebrow">{t('footer.faq')}</span>
            <h2>{t('landing.faq_title')}</h2>
          </div>
          <div className="reveal d1"><Faq items={faqItems} /></div>
        </Section>

        {/* CTA */}
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="cta-band">
              <h2>{t('landing.cta_title')}</h2>
              <p>{t('landing.cta_sub')}</p>
              <Link to="/register" className="btn btn-lg">
                {t('landing.cta_button')} <Icon name="arrow" size={15} />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <CorpFooter />
    </>
  );
}
